import React, { useState } from 'react';
import { Box, Avatar, Typography, Tooltip, Checkbox, ButtonBase } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { PurchaseOrderItem } from '@domain/purchasing';
import { APP_CONFIG } from '@config/app.config';

interface SelectableProductDisplayProps {
  purchaseOrderItems: {
    item: PurchaseOrderItem;
    selected?: boolean;
    disabled?: boolean;
  }[];
  onSelectionChange?: (selectedItems: PurchaseOrderItem[]) => void;
  showCheckbox?: boolean;
}

export const SelectableProductDisplay: React.FC<SelectableProductDisplayProps> = ({
  purchaseOrderItems,
  onSelectionChange,
  showCheckbox = true
}) => {
  const [purchaseOrderItemsState, setPurchaseOrderItemsState] = useState<{
    item: PurchaseOrderItem;
    selected?: boolean;
    disabled?: boolean;
  }[]>(purchaseOrderItems);

  const isSelected = (item: { item: PurchaseOrderItem; selected?: boolean; disabled?: boolean; }) => {
    return item.selected || false;
  };

  const toggleSelectPurchaseOrderItem = (item: { item: PurchaseOrderItem; selected?: boolean; disabled?: boolean; }) => {
    if (item.disabled) return;

    const updatedItems = purchaseOrderItemsState.map(i => {
      if (i.item.id === item.item.id) {
        return {
          ...i,
          selected: !i.selected
        };
      }
      return i;
    });

    setPurchaseOrderItemsState(updatedItems);

    // Notify parent component of selection changes
    if (onSelectionChange) {
      const selectedItems = updatedItems
        .filter(item => item.selected && !item.disabled)
        .map(item => item.item);
      onSelectionChange(selectedItems);
    }
  };

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap={2} flexWrap="wrap">
      {purchaseOrderItemsState.map((item) => (
        <Box
          component={ButtonBase}
          onClick={() => toggleSelectPurchaseOrderItem(item)}
          display="flex"
          alignItems="center"
          gap={2}
          key={item.item.product.id}
          sx={{
            borderRadius: 2,
            p: 2,
            border: '1px solid',
            borderColor: isSelected(item) ? APP_CONFIG.ui.theme.colors.primary : APP_CONFIG.ui.theme.colors.border,
            width: 250,
            opacity: item.disabled ? 0.6 : 1,
            cursor: item.disabled ? 'not-allowed' : 'pointer',
          }}
        >
          {showCheckbox && (
            <Checkbox
              checked={isSelected(item)}
              disabled={item.disabled}
              onChange={() => toggleSelectPurchaseOrderItem(item)}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          <Avatar
            variant="rounded"
            sx={{
              border: 1,
              borderColor: APP_CONFIG.ui.theme.colors.border,
              width: 30,
              height: 30,
            }}
            src={item.item.product.image}
            alt={item.item.product.title}
          />

          <Tooltip title={item.item.product.title}>
            <Typography
              variant="caption"
              sx={{
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.item.product.title}
            </Typography>
          </Tooltip>

          {item.item.quantity && (
            <Box>
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.item.quantity} items
              </Typography>
            </Box>
          )}

          {item.item.shipment && (
            <Tooltip title="Item will be moved from its current shipment">
              <Box>
                <Warning color="warning" />
              </Box>
            </Tooltip>
          )}
        </Box>
      ))}
    </Box>
  );
}; 